import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ClientSessionService } from '@brighthub/uisession';
import { CopilotContextComposerService } from '@brighthub/copilot';
import { ClientResourcesStateService } from './client-resources-state.service';
import {
  ConfidenceLevel,
  ELIGIBILITY_STATUSES,
  EligibilityStatus,
  RankedResource,
  RESOURCE_CATEGORIES,
  RESOURCE_TYPES,
  SORT_OPTIONS,
  ResourceCategory,
  ResourceFilters,
  ResourceSortKey,
  ResourceType,
  categoryLabel,
  confidenceLevelLabel,
  eligibilityStatusSeverity,
} from './client-resources.model';

@Component({
  selector: 'bai-client-resources',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, SkeletonModule, TagModule, TooltipModule],
  templateUrl: './client-resources.component.html',
  styleUrl: './client-resources.component.scss',
})
export class ClientResourcesComponent {
  private readonly _session = inject(ClientSessionService);
  private readonly _stateService = inject(ClientResourcesStateService);
  private readonly _copilotComposer = inject(CopilotContextComposerService);

  readonly loading = signal(true);

  // Catalogue options for filter dropdowns
  readonly resourceTypes    = RESOURCE_TYPES;
  readonly resourceCategories = RESOURCE_CATEGORIES;
  readonly eligibilityStatuses = ELIGIBILITY_STATUSES;
  readonly sortOptions      = SORT_OPTIONS;

  /** Expanded "why shown" row ids. */
  readonly expandedWhy = signal<Set<string>>(new Set());

  private readonly _sessionId = computed(
    () => this._session.selectedSession()?.uiSessionId ?? null
  );

  constructor() {
    effect(() => {
      const id = this._sessionId();
      if (id) {
        this._stateService.ensureState(id);
        this.loading.set(false);
      } else {
        this.loading.set(false);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Reactive state
  // ---------------------------------------------------------------------------

  readonly state = computed(() => {
    const id = this._sessionId();
    if (!id) return null;
    // Read storeSnapshot to track reactivity
    this._stateService.storeSnapshot();
    return this._stateService.getState(id);
  });

  readonly employerResources = computed(() => {
    const id = this._sessionId();
    if (!id) return [];
    this._stateService.storeSnapshot();
    return this._stateService.employerResources(id);
  });

  readonly communityResources = computed(() => {
    const id = this._sessionId();
    if (!id) return [];
    this._stateService.storeSnapshot();
    return this._stateService.communityResources(id);
  });

  readonly filteredForExplorer = computed(() => {
    const id = this._sessionId();
    if (!id) return [];
    this._stateService.storeSnapshot();
    return this._stateService.filteredResources(id);
  });

  readonly explorerPage = computed(() => this.state()?.explorerPage ?? 0);
  readonly explorerPageSize = 10;

  readonly explorerPageItems = computed(() => {
    const all = this.filteredForExplorer();
    const page = this.explorerPage();
    return all.slice(page * this.explorerPageSize, (page + 1) * this.explorerPageSize);
  });

  readonly explorerTotalPages = computed(() =>
    Math.ceil(this.filteredForExplorer().length / this.explorerPageSize)
  );

  readonly filters = computed(() => this.state()?.filters ?? null);

  // ---------------------------------------------------------------------------
  // Filter actions
  // ---------------------------------------------------------------------------

  setSearch(value: string): void {
    const id = this._sessionId();
    if (id) this._stateService.setFilter(id, 'search', value);
  }

  setTypeFilter(value: ResourceType | 'All'): void {
    const id = this._sessionId();
    if (id) this._stateService.setFilter(id, 'type', value);
  }

  setCategoryFilter(value: ResourceCategory | 'All'): void {
    const id = this._sessionId();
    if (id) this._stateService.setFilter(id, 'category', value);
  }

  setEligibilityFilter(value: EligibilityStatus | 'All'): void {
    const id = this._sessionId();
    if (id) this._stateService.setFilter(id, 'eligibilityStatus', value);
  }

  setSortKey(value: ResourceSortKey): void {
    const id = this._sessionId();
    if (id) this._stateService.setFilter(id, 'sortKey', value);
  }

  setExplorerPage(page: number): void {
    const id = this._sessionId();
    if (id) this._stateService.setExplorerPage(id, page);
  }

  // ---------------------------------------------------------------------------
  // Row actions
  // ---------------------------------------------------------------------------

  toggleSaved(resource: RankedResource): void {
    const id = this._sessionId();
    if (id) this._stateService.toggleSaved(id, resource.resourceId);
  }

  callResource(resource: RankedResource): void {
    const id = this._sessionId();
    if (id) this._stateService.recordContact(id, resource.resourceId, 'phone');
    if (resource.contactPhone) window.open(`tel:${resource.contactPhone}`);
  }

  emailResource(resource: RankedResource): void {
    const id = this._sessionId();
    if (id) this._stateService.recordContact(id, resource.resourceId, 'email');
    if (resource.contactEmail) window.open(`mailto:${resource.contactEmail}`);
  }

  openWebsite(resource: RankedResource): void {
    const id = this._sessionId();
    if (id) this._stateService.recordContact(id, resource.resourceId, 'web');
    if (resource.websiteUrl) window.open(resource.websiteUrl, '_blank', 'noopener');
  }

  toggleWhy(resourceId: string): void {
    this.expandedWhy.update(s => {
      const next = new Set(s);
      if (next.has(resourceId)) next.delete(resourceId);
      else next.add(resourceId);
      return next;
    });
  }

  isWhyExpanded(resourceId: string): boolean {
    return this.expandedWhy().has(resourceId);
  }

  // ---------------------------------------------------------------------------
  // Display helpers
  // ---------------------------------------------------------------------------

  eligibilitySeverity(status: EligibilityStatus): 'success' | 'warn' | 'danger' | 'secondary' {
    return eligibilityStatusSeverity(status);
  }

  confidenceLabel(level: ConfidenceLevel): string {
    return confidenceLevelLabel(level);
  }

  categoryDisplayLabel(cat: ResourceCategory): string {
    return categoryLabel(cat);
  }

  typeIcon(type: ResourceType): string {
    switch (type) {
      case 'Employer':  return 'pi pi-building';
      case 'Community': return 'pi pi-users';
      case 'Other':     return 'pi pi-globe';
    }
  }

  rankScoreClass(score: number): string {
    if (score >= 75) return 'bai-res__rank--high';
    if (score >= 50) return 'bai-res__rank--medium';
    return 'bai-res__rank--low';
  }

  formatDate(iso: string): string {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    }).format(new Date(iso));
  }

  trackById(_: number, item: { resourceId: string }): string {
    return item.resourceId;
  }

  pageRange(total: number): number[] {
    return Array.from({ length: total }, (_, i) => i);
  }

  filterCount(filters: ResourceFilters): number {
    let n = 0;
    if (filters.type !== 'All') n++;
    if (filters.category !== 'All') n++;
    if (filters.eligibilityStatus !== 'All') n++;
    if (filters.search.trim()) n++;
    return n;
  }

  clearFilters(): void {
    const id = this._sessionId();
    if (!id) return;
    this._stateService.setFilter(id, 'type', 'All');
    this._stateService.setFilter(id, 'category', 'All');
    this._stateService.setFilter(id, 'eligibilityStatus', 'All');
    this._stateService.setFilter(id, 'search', '');
  }

  addResourceToCopilot(resource: RankedResource): void {
    const preview = this.truncate(resource.name, 20);
    this._copilotComposer.addContext({
      sourceType: 'resources',
      sourceId: resource.resourceId,
      label: `Resource: ${preview}`,
      fullText:
        `[Resources | ${resource.resourceId}] ` +
        `Name: ${resource.name}; Provider: ${resource.providerName}; Type: ${resource.type}; ` +
        `Category: ${this.categoryDisplayLabel(resource.category)}; Rank: ${resource.rankScore}; ` +
        `Eligibility: ${resource.eligibilityStatus}; Why shown: ${resource.whyShownText}`,
    });
  }

  truncate(value: string, max = 20): string {
    if (value.length <= max) return value;
    return `${value.slice(0, max)}…`;
  }
}
